import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { Row, Col, Card, Button, Badge, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../layouts/PageLayout";
import "suneditor/dist/css/suneditor.min.css";

const normalizeProductAttributes = (attributes) => {
    const entries = Array.isArray(attributes)
        ? attributes.map((attribute) => [attribute?.name, attribute?.value])
        : attributes && typeof attributes === "object"
            ? Object.entries(attributes).map(([name, value]) => [
                value && typeof value === "object" ? value.name ?? name : name,
                value && typeof value === "object" ? value.value : value,
            ])
            : [];

    return entries
        .filter(([name]) => name !== null && name !== undefined && String(name).trim() !== "")
        .map(([name, value]) => ({
            name: String(name),
            value: value === null || value === undefined || value === ""
                ? "—"
                : typeof value === "object"
                    ? value.name || value.title || "—"
                    : String(value),
        }));
};

export default function ListerProductDetailPage() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [product, setProduct] = useState(location.state?.item || location.state?.product || null);
    const [loading, setLoading] = useState(!product);
    const [error, setError] = useState(null);

    const [, setStores] = useState([]);
    const [, setBrands] = useState([]);
    const [, setInitialLoading] = useState(false);

    const getValue = (field) => {
        if (field === null || field === undefined || field === "") return null;
        if (typeof field === "object") {
            return field.name || field.title || null;
        }
        return field;
    };

    const cleanHtmlContent = (htmlString) => {
        if (!htmlString) return "";
        try {
            let unescaped = htmlString
                .replace(/\\"/g, '"')
                .replace(/\\\//g, "/")
                .replace(/\\r\\n/g, "")
                .replace(/\\n/g, "")
                .replace(/\\t/g, "");

            const parser = new DOMParser();
            const doc = parser.parseFromString(unescaped, "text/html");
            return doc.body.innerHTML;
        } catch (e) {
            console.error("Error cleaning HTML content:", e);
            return htmlString;
        }
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            setInitialLoading(true);
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/lister/initial-data`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        },
                    }
                );
                const resData = response.data;
                if (resData.success || resData.data) {
                    const data = resData.data || resData;
                    setStores(data.stores || []);
                    setBrands(data.brands || []);
                }
            } catch (err) {
                console.error("Error fetching initial data:", err);
            } finally {
                setInitialLoading(false);
            }
        };

        fetchInitialData();
    }, []);

    const fetchProductById = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/lister/products/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            const resData = response.data;
            if (resData.success || resData.data) {
                const data = Array.isArray(resData.data) ? resData.data[0] : (resData.data || resData);
                setProduct(data);
            } else {
                setError(resData.message || "Failed to fetch product details.");
            }
        } catch (err) {
            console.error("Error fetching lister product details:", err);
            setError(
                err.response?.data?.message || "Failed to load product details."
            );
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (!product && id) {
            fetchProductById();
        }
    }, [id, product, fetchProductById]);

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("loading_product_details...")}</p>
                </div>
            </PageLayout>
        );
    }

    if (error || !product) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Alert variant="danger" className="d-inline-block px-5">
                        {error || t("no product data available")}
                    </Alert>
                    <div>
                        <Button
                            variant="primary"
                            className="mt-3"
                            onClick={() => navigate(-1)}
                        >
                            {t("back to list")}
                        </Button>
                    </div>
                </div>
            </PageLayout>
        );
    }

    const categoryVal = getValue(product.category);
    const brandVal = getValue(product.brand);
    const storeVal = getValue(product.store);
    const listerVal = getValue(product.lister) || getValue(product.lister_name);
    const marketPlacerVal = getValue(product.marketPlacer);

    const galleryImages = Array.isArray(product.images) ? product.images : [];
    const productAttributes = normalizeProductAttributes(product.attributes);

    return (
        <PageLayout>
            <style>
                {`
                    .sun-editor-editable table {
                        width: 100% !important;
                        border-collapse: collapse !important;
                        margin: 15px 0 !important;
                    }
                    .sun-editor-editable table td, 
                    .sun-editor-editable table th {
                        border: 1px solid #dcdcdc !important;
                        padding: 8px 12px !important;
                        vertical-align: middle !important;
                    }
                `}
            </style>

            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {t("lister product details")}
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("home")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link
                                            to="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(-1);
                                            }}
                                            className="mc-breadcrumb-link"
                                        >
                                            {t("lister products")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        {t("product details")}
                                    </li>
                                </ul>
                            </div>
                            <Button
                                variant="secondary"
                                onClick={() => navigate(-1)}
                            >
                                {t("back")}
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col lg={4}>
                    <Card className="mb-4">
                        <Card.Body className="text-center">

                            {product.title && <h5>{product.title}</h5>}
                            {product.product_code && (
                                <Badge bg="primary" className="mb-2">
                                    {product.product_code}
                                </Badge>
                            )}

                            {product.rejection_reason && (
                                <div className="alert alert-danger py-2 px-3 my-2 text-start">
                                    <strong>{t("rejection reason")}: </strong>
                                    <span>{product.rejection_reason}</span>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {product.card_img && (
                        <Card className="mb-4">
                            <Card.Header className="bg-white">
                                <h6 className="mb-0">{t("card image")}</h6>
                            </Card.Header>
                            <Card.Body className="text-center">
                                <img
                                    src={`${process.env.REACT_APP_IMG_URL}${product.card_img}`}
                                    alt="Card Preview"
                                    className="img-fluid rounded"
                                    style={{ maxHeight: "180px", objectFit: "cover" }}
                                />
                            </Card.Body>
                        </Card>
                    )}
                </Col>

                <Col lg={8}>
                    <Card className="mb-4">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">{t("product information")}</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3">
                                {categoryVal && (
                                    <Col sm={6}>
                                        <strong>{t("category")}: </strong>
                                        <span>{categoryVal}</span>
                                    </Col>
                                )}

                                {brandVal && (
                                    <Col sm={6}>
                                        <strong>{t("brand")}: </strong>
                                        <span>{brandVal}</span>
                                    </Col>
                                )}

                                {listerVal && (
                                    <Col sm={6}>
                                        <strong>{t("lister")}: </strong>
                                        <span>{listerVal}</span>
                                    </Col>
                                )}

                                {storeVal && (
                                    <Col sm={6}>
                                        <strong>{t("store")}: </strong>
                                        <span>{storeVal}</span>
                                    </Col>
                                )}

                                {marketPlacerVal && (
                                    <Col sm={6}>
                                        <strong>{t("market placer")}: </strong>
                                        <span>{marketPlacerVal}</span>
                                    </Col>
                                )}

                                {product.selling_style && (
                                    <Col sm={6}>
                                        <strong>{t("selling style")}: </strong>
                                        <span>{product.selling_style}</span>
                                    </Col>
                                )}

                                {product.product_condition && (
                                    <Col sm={6}>
                                        <strong>{t("condition")}: </strong>
                                        <span>{product.product_condition}</span>
                                    </Col>
                                )}

                                {product.product_qty !== null && product.product_qty !== undefined && (
                                    <Col sm={6}>
                                        <strong>{t("quantity")}: </strong>
                                        <span>{product.product_qty}</span>
                                    </Col>
                                )}

                                <Col sm={6}>
                                    <strong>{t("sell price dollar")}: </strong>
                                    {product?.sell_price_dollar === null ||
                                        product?.sell_price_dollar === undefined ||
                                        product?.sell_price_dollar === "" ||
                                        product?.sell_price_dollar === 0 ||
                                        product?.sell_price_dollar === "0" ? (
                                        <span className="badge bg-warning text-dark fw-bold">Study</span>
                                    ) : (
                                        <span>${product.sell_price_dollar}</span>
                                    )}
                                </Col>

                                {product.shipping_charges_dollar !== null && product.shipping_charges_dollar !== undefined && (
                                    <Col sm={6}>
                                        <strong>{t("shipping charges dollar")}: </strong>
                                        <span>${product.shipping_charges_dollar}</span>
                                    </Col>
                                )}
                            </Row>

                            {productAttributes.length > 0 && (
                                <div className="mt-4">
                                    <h5 className="mb-3">{t("product attributes")}</h5>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered align-middle mb-0">
                                            <tbody>
                                                {productAttributes.map((attribute, index) => (
                                                    <tr key={`${attribute.name}-${index}`}>
                                                        <th scope="row" className="w-50">{attribute.name}</th>
                                                        <td>{attribute.value}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {product.short_description && (
                                <div className="mt-4">
                                    <strong>{t("short description")}: </strong>
                                    <div
                                        className="sun-editor-editable text-muted mb-0"
                                        dangerouslySetInnerHTML={{ __html: cleanHtmlContent(product.short_description) }}
                                    />
                                </div>
                            )}

                            {product.description && (
                                <div className="mt-4">
                                    <strong>{t("description")}: </strong>
                                    <div
                                        className="sun-editor-editable text-muted mb-0"
                                        dangerouslySetInnerHTML={{ __html: cleanHtmlContent(product.description) }}
                                    />
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {galleryImages.length > 0 && (
                        <Card className="mb-4">
                            <Card.Header className="bg-white">
                                <h5 className="mb-0">{t("product gallery")}</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    {galleryImages.map((imgObj, idx) => (
                                        <Col key={imgObj.id || idx} xs={6} sm={4} md={3}>
                                            <img
                                                src={`${process.env.REACT_APP_IMG_URL}${imgObj.image || imgObj}`}
                                                alt={`Gallery ${idx + 1}`}
                                                className="img-fluid rounded border"
                                                style={{ height: "120px", width: "100%", objectFit: "cover" }}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>
        </PageLayout>
    );
}